import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../contexts/notifications/NotificationContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Define notification item interface
interface NotificationItem {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  read: boolean;
  created_at: Date;
  data?: any;
}

const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Handle notification click
  const handleNotificationClick = (notification: NotificationItem) => {
    markAsRead(notification.id);
    
    // Navigate to schedule page for booking notifications
    if (notification.notification_type === 'booking_confirmed') {
      navigate('/admin/schedule');
    }
    
    setIsOpen(false);
  };
  
  // Handle mark all as read
  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllAsRead();
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell icon with notification counter */}
      <button
        className="relative p-2 text-gray-700 hover:text-[#3A5E63] focus:outline-none bg-gray-100 rounded-full"
        onClick={() => {
          setIsOpen(!isOpen);
          // Request notification permission when bell is clicked
          if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
            Notification.requestPermission();
          }
        }}
        aria-label="Notifications"
      >
        <span className="text-xl">🔔</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg overflow-hidden z-50">
          <div className="p-3 bg-gray-50 border-b flex justify-between items-center">
            <h3 className="font-medium">Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="text-xs text-[#3A5E63] hover:text-[#2c474b]"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start">
                      <div className={`mt-1 mr-3 flex-shrink-0 ${
                        notification.notification_type === 'booking_confirmed' ? 'text-green-500' :
                        notification.notification_type === 'booking_cancelled' ? 'text-red-500' : 'text-blue-500'
                      }`}>
                        {notification.notification_type === 'booking_confirmed' ? '✅' :
                         notification.notification_type === 'booking_cancelled' ? '❌' : 'ℹ️'}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{notification.title}</div>
                        <div className="text-sm text-gray-600">{notification.message}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {format(notification.created_at, 'MMM d, h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;